import React, { useState, useEffect, useCallback } from 'react';
import { getLoginAuditoria } from '../services/loginAuditoriaService';
import { getSucursales } from '../services/sucursalService';
import { getUsuariosPaginados } from '../services/usuarioService';
import { 
    ArrowLeft, Search, RefreshCw, Filter, Calendar, 
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Monitor, ShieldCheck, ShieldAlert, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function LoginAuditoriaList() {
    const [audits, setAudits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sucursales, setSucursales] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    
    // Pagination
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Filters
    const [filters, setFilters] = useState({
        usuarioId: '',
        sucursalId: '',
        desde: '',
        hasta: ''
    });

    const navigate = useNavigate();

    const cargarCatalogos = async () => {
        try {
            const [dataSuc, dataUsu] = await Promise.all([
                getSucursales(),
                getUsuariosPaginados(0, 1000) // All users for the filter
            ]);
            setSucursales(dataSuc);
            setUsuarios(dataUsu.content || []);
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
        }
    };

    const cargarAuditoria = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getLoginAuditoria(filters, page, pageSize);
            setAudits(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
        } catch (error) {
            console.error('Error al cargar auditoría:', error);
            toast.error('No se pudo cargar la auditoría de accesos');
        } finally {
            setLoading(false);
        }
    }, [filters, page, pageSize]);

    useEffect(() => {
        cargarCatalogos();
    }, []);

    useEffect(() => {
        cargarAuditoria();
    }, [cargarAuditoria]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(0); // Reset to first page on filter change
    };

    const resetFilters = () => {
        setFilters({
            usuarioId: '',
            sucursalId: '',
            desde: '',
            hasta: ''
        });
        setPage(0);
    };

    const formatSeconds = (seconds) => {
        if (!seconds) return 'N/A';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'm ' : ''}${s}s`;
    };

    return (
        <div className="max-w-7xl mx-auto bg-white shadow-xl border border-gray-200 mb-10 rounded-lg overflow-hidden animate-in fade-in duration-500">
            <Toaster position="top-right" />

            {/* Premium Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-5 flex justify-between items-center text-white border-b border-slate-700">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-slate-700 rounded-full transition-all text-slate-300 hover:text-white group"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-blue-400" />
                            Auditoría de Accesos
                        </h1>
                        <p className="text-xs text-slate-400 font-medium uppercase tracking-widest mt-0.5">Seguridad Bancaria • Registros Inalterables</p>
                    </div>
                </div>

                <button
                    onClick={cargarAuditoria}
                    disabled={loading}
                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                    title="Refrescar"
                >
                    <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Advanced Filters Bento Area */}
            <div className="p-5 bg-[#f8fafc] border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* User Filter */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                            <Search className="w-3 h-3" /> Usuario
                        </label>
                        <select
                            name="usuarioId"
                            value={filters.usuarioId}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm rounded-lg transition-all"
                        >
                            <option value="">Todos los usuarios</option>
                            {usuarios.map(u => (
                                <option key={u.id} value={u.id}>{u.nombreUsuario} - {u.nombre} {u.apellido}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sucursal Filter */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                            <Monitor className="w-3 h-3" /> Sucursal
                        </label>
                        <select
                            name="sucursalId"
                            value={filters.sucursalId}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 p-2.5 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm rounded-lg transition-all"
                        >
                            <option value="">Todas las sucursales</option>
                            {sucursales.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date range - Start */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Desde
                        </label>
                        <input
                            type="datetime-local"
                            name="desde"
                            value={filters.desde}
                            onChange={handleFilterChange}
                            className="w-full border border-gray-300 p-2 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm rounded-lg transition-all"
                        />
                    </div>

                    {/* Date range - End */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Hasta
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="datetime-local"
                                name="hasta"
                                value={filters.hasta}
                                onChange={handleFilterChange}
                                className="w-full border border-gray-300 p-2 bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm rounded-lg transition-all"
                            />
                            <button
                                onClick={resetFilters}
                                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors shadow-sm"
                                title="Limpiar Filtros"
                            >
                                <Filter className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="overflow-x-auto relative min-h-[400px]">
                {loading && audits.length === 0 ? (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 backdrop-blur-sm">
                        <div className="flex flex-col items-center gap-3">
                            <RefreshCw className="w-10 h-10 text-blue-600 animate-spin" />
                            <p className="text-sm font-medium text-gray-500 animate-pulse">Cargando registros de seguridad...</p>
                        </div>
                    </div>
                ) : null}

                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-[#f1f5f9] border-b border-gray-200 text-slate-600 text-[10px] uppercase font-bold tracking-widest">
                            <th className="px-6 py-4">Usuario</th>
                            <th className="px-6 py-4">Sucursal</th>
                            <th className="px-6 py-4">Acceso (Login)</th>
                            <th className="px-6 py-4">Salida (Logout)</th>
                            <th className="px-6 py-4">Duración</th>
                            <th className="px-6 py-4">Dirección IP</th>
                            <th className="px-6 py-4">Estado</th>
                            <th className="px-6 py-4">Dispositivo</th>
                        </tr>
                    </thead>
                    <tbody className="text-[13px] text-slate-600 divide-y divide-gray-100">
                        {audits.length === 0 && !loading ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-20 text-center">
                                    <div className="flex flex-col items-center gap-2 opacity-40">
                                        <ShieldAlert className="w-12 h-12" />
                                        <p className="text-lg font-medium">No se encontraron registros de acceso</p>
                                        <p className="text-xs">Ajuste los filtros para ampliar la búsqueda</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            audits.map((audit) => (
                                <tr key={audit.id} className="hover:bg-blue-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors uppercase tracking-tight">
                                            {audit.usuarioNombre}
                                        </div>
                                        <div className="text-[10px] text-slate-400 font-medium">ID: #{audit.usuarioId}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[11px] font-bold">
                                            {audit.sucursalNombre}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium">
                                            {audit.fechaAcceso ? format(new Date(audit.fechaAcceso), 'dd/MM/yyyy HH:mm:ss') : 'N/A'}
                                        </div>
                                        <div className="text-[10px] text-blue-500 font-bold uppercase">{format(new Date(audit.fechaAcceso), 'EEEE', { locale: es })}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-slate-500">
                                            {audit.fechaSalida ? format(new Date(audit.fechaSalida), 'dd/MM/yyyy HH:mm:ss') : (
                                                <span className="flex items-center gap-1.5 text-emerald-600 font-bold animate-pulse">
                                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                                                    Activo
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {audit.duracionSesion ? (
                                            <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                                {formatSeconds(audit.duracionSesion)}
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-[11px] text-slate-500">
                                        {audit.direccionIp}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${
                                            audit.estadoAcceso === 'EXITOSO' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                                            audit.estadoAcceso === 'FALLIDO' ? 'bg-rose-100 text-rose-700 border border-rose-200' :
                                            'bg-amber-100 text-amber-700 border border-amber-200'
                                        }`}>
                                            {audit.estadoAcceso}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 max-w-[200px]">
                                        <div className="truncate text-slate-400 italic text-[11px]" title={audit.dispositivo}>
                                            {audit.dispositivo}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Premium Footer */}
            <div className="p-4 bg-[#f8fafc] border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-widest">
                    Mostrando <span className="text-slate-900 font-bold">{totalElements === 0 ? 0 : page * pageSize + 1}</span> a <span className="text-slate-900 font-bold">{Math.min((page + 1) * pageSize, totalElements)}</span> de <span className="text-slate-900 font-bold">{totalElements}</span> registros
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 mr-4 border-r border-gray-300 pr-4">
                        <label className="text-[10px] font-bold text-gray-500 uppercase">Filas:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(0);
                            }}
                            className="bg-white border border-gray-300 text-xs rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-blue-500/20"
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setPage(0)}
                            disabled={page === 0 || loading}
                            className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition-colors"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPage(p => p - 1)}
                            disabled={page === 0 || loading}
                            className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        
                        <div className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-300 rounded-lg shadow-sm">
                            <span className="text-xs font-bold text-blue-600">{page + 1}</span>
                            <span className="text-[10px] font-medium text-gray-400 uppercase">de</span>
                            <span className="text-xs font-bold text-gray-600">{totalPages || 1}</span>
                        </div>

                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={page >= totalPages - 1 || loading}
                            className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPage(totalPages - 1)}
                            disabled={page >= totalPages - 1 || loading}
                            className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-30 transition-colors"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
